import { useState, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { MobileTabBar } from '../../pages/professor/MobileTabBar';
import { useUnreadNotifications } from '../notifications/InAppNotificationWatcher';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < breakpoint : false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

interface MobilePageWrapperProps {
  /** Titulo exibido no header mobile */
  title: string;
  /** Icone lucide ao lado do titulo */
  icon?: LucideIcon;
  /** Cor do icone */
  iconColor?: string;
  /** Rota de voltar (default: /professor/dashboard) */
  backTo?: string;
  /** Se true, oculta o botao de voltar (para paginas acessiveis direto pela tab bar) */
  hideBackButton?: boolean;
  /** Badge/counter no header */
  badge?: string | number;
  /** Cor do badge */
  badgeColor?: string;
  /** Se true, mostra indicador de pendencias no tab bar */
  hasPendencias?: boolean;
  /** Conteudo da pagina */
  children: ReactNode;
  /** Se true, nao mostra o wrapper desktop (admin-page) */
  mobileOnly?: boolean;
  /** Hero customizado na pagina — oculta o header padrao no mobile */
  suppressHeader?: boolean;
}

/**
 * Wrapper que fornece layout mobile premium para sub-paginas do professor.
 * No desktop (>= 768px), renderiza o children normalmente.
 * No mobile (< 768px), adiciona:
 *   - Sticky header card com gradient navy: icone + titulo a esquerda, voltar a direita
 *   - Scroll area com padding para tab bar
 *   - Bottom tab bar (Apple style)
 *
 * REGRA: Nenhuma acao (botao +, filtro, etc) no header. Acoes ficam no corpo da pagina.
 */
export function MobilePageWrapper({
  title,
  icon: Icon,
  iconColor = '#fff',
  backTo = '/professor/dashboard',
  hideBackButton = false,
  badge,
  badgeColor,
  hasPendencias = false,
  children,
  mobileOnly = false,
  suppressHeader = false,
}: MobilePageWrapperProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { unreadCount } = useUnreadNotifications();

  // Desktop: render normal
  if (!isMobile && !mobileOnly) {
    return <>{children}</>;
  }

  return (
    <div className="mobile-page-root">
      {/* ── Sticky Header: [Icon Title Badge] ... [← Voltar] ── */}
      {!suppressHeader && (
      <div className="mobile-page-header">
        <div className="mobile-page-title-area">
          {Icon && <Icon size={18} style={{ color: iconColor, flexShrink: 0 }} />}
          <span className="mobile-page-title">{title}</span>
          {badge !== undefined && badge !== null && (
            <span
              className="mobile-page-badge"
              style={badgeColor ? { background: badgeColor + '20', color: badgeColor } : undefined}
            >
              {badge}
            </span>
          )}
        </div>
        {!hideBackButton && (
          <button
            className="mobile-page-back"
            onClick={() => navigate(backTo)}
            aria-label="Voltar"
          >
            <ChevronLeft size={22} strokeWidth={2.5} />
          </button>
        )}
      </div>
      )}

      {/* ── Scroll Content ── */}
      <div className="mobile-page-content">
        {children}
      </div>

      {/* ── Bottom Tab Bar ── */}
      <MobileTabBar hasPendencias={hasPendencias} unreadComunicados={unreadCount} />
    </div>
  );
}
